import React, { useRef } from 'react';
import { Button, Progress } from 'reactstrap';

const ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.log,.csv';
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5;

const ALLOWED_EXTENSIONS = new Set(
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'txt', 'log', 'csv']
);

function getExtension(name) {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileAttachmentZone({
  files = [],
  onChange,
  error,
  disabled,
  uploadProgress,
  maxFiles = MAX_FILES,
  label = 'Attachments (optional, max 5 files, 10 MB each)',
}) {
  const inputRef = useRef(null);

  const validateFile = (file) => {
    const ext = getExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return `File type not allowed: ${file.name}. Use images, PDF, Word, text, log, or CSV.`;
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `${file.name} exceeds 10 MB.`;
    }
    return null;
  };

  const handleSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    if (selected.length === 0) return;
    const next = [...files];
    const errs = [];
    for (const file of selected) {
      if (next.length >= maxFiles) {
        errs.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.`);
        break;
      }
      const msg = validateFile(file);
      if (msg) errs.push(msg);
      else next.push(file);
    }
    onChange(next, errs.length ? errs : null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    const items = e.dataTransfer?.files;
    if (!items?.length) return;
    const selected = Array.from(items);
    const next = [...files];
    const errs = [];
    for (const file of selected) {
      if (next.length >= maxFiles) {
        errs.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed.`);
        break;
      }
      const msg = validateFile(file);
      if (msg) errs.push(msg);
      else next.push(file);
    }
    onChange(next, errs.length ? errs : null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const remove = (index) => {
    const next = files.filter((_, i) => i !== index);
    onChange(next, null);
  };

  return (
    <div className="mt-2">
      <label className="col-form-label d-block">{label}</label>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border border-2 border-dashed rounded p-3 text-center${error ? ' border-danger' : ''}${disabled ? '' : ' cursor-pointer bg-light'}`}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
        role="button"
        aria-label="Add files (drag and drop or click)"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={handleSelect}
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          aria-label="Choose files"
        />
        <i className="fa fa-paperclip fa-2x text-muted" aria-hidden="true" />
        <p className="text-muted small mb-0 mt-2">
          Drag and drop or click to browse. Images, PDF, Word, text, log, CSV.
        </p>
      </div>
      {error && (
        <small className="text-danger d-block mt-1" role="alert">
          {Array.isArray(error) ? error.join(' ') : error}
        </small>
      )}
      {files.length > 0 && (
        <ul className="list-unstyled mt-2 mb-0">
          {files.map((file, i) => (
            <li
              key={i}
              className="d-flex align-items-center justify-content-between py-1 border-bottom"
            >
              <span className="text-truncate me-2">
                {file.name} ({formatSize(file.size)})
              </span>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                onClick={(e) => { e.stopPropagation(); remove(i); }}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {uploadProgress != null && uploadProgress < 100 && (
        <div className="mt-2">
          <small className="text-muted">Uploading…</small>
          <Progress value={uploadProgress} className="mt-1" />
        </div>
      )}
    </div>
  );
}

export { MAX_FILES, MAX_SIZE_BYTES };
